// Detects when a newer build has been deployed while the app is open and offers
// the user a one-click reload.
//
// The build emits a stable, unhashed `version.json` (see versionJsonPlugin in
// vite.config.ts). We poll it and compare the deployed version against this build's
// compile-time __APP_VERSION__. On a mismatch we show a persistent notification with
// a reload button — combined with prefetch-views this means the session never breaks
// silently: it either keeps working (prefetched chunks) or the user is told to reload.

import { h, onMounted, onBeforeUnmount } from "vue";
import { ElNotification, ElButton } from "element-plus";
import { t } from "@/lang";

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

async function fetchDeployedVersion(): Promise<string | null> {
  try {
    // Cache-buster + no-store so we always see the freshly deployed value, never a
    // heuristically cached copy. In dev there is no version.json → fetch fails/404
    // and we simply return null (no notification).
    const res = await fetch(`/version.json?ts=${Date.now()}`, {
      cache: "no-store",
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { version?: unknown };
    return typeof data.version === "string" ? data.version : null;
  } catch {
    return null;
  }
}

export function useVersionCheck(): void {
  const current = __APP_VERSION__;
  let notified = false;
  let timer: ReturnType<typeof setInterval> | undefined;

  function notifyNewVersion(): void {
    ElNotification({
      title: t("update.new_version_title"),
      type: "info",
      position: "bottom-right",
      duration: 0, // stays until the user acts
      showClose: true,
      message: h("div", [
        h(
          "p",
          { style: "margin: 0 0 8px" },
          t("update.new_version_message"),
        ),
        h(
          ElButton,
          {
            type: "primary",
            size: "small",
            onClick: () => window.location.reload(),
          },
          () => t("buttons.update"),
        ),
      ]),
    });
  }

  async function check(): Promise<void> {
    if (notified) return;
    const deployed = await fetchDeployedVersion();
    // Re-check `notified` AFTER the await: several triggers (initial mount,
    // interval, focus, visibilitychange) can fire check() near-simultaneously and
    // each pass the pre-fetch guard while a fetch is in flight. Without this second
    // guard they would each show a toast (duplicate notifications). There is no
    // await between this check and setting the flag, so only the first one wins.
    if (notified || !deployed || deployed === current) return;
    notified = true;
    notifyNewVersion();
  }

  function onVisibilityChange(): void {
    if (document.visibilityState === "visible") check();
  }

  onMounted(() => {
    // Check once on start, then on an interval and whenever the user returns to the
    // tab (covers the common "left it open for a while" case cheaply).
    check();
    timer = setInterval(check, POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", check);
  });

  onBeforeUnmount(() => {
    if (timer) clearInterval(timer);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("focus", check);
  });
}
